import { ChakraProvider, ColorModeScript, createStandaloneToast } from '@chakra-ui/react';
import { Worker } from '@react-pdf-viewer/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import './index.css';
import './i18n/i18n.ts';
// Reskin fonts (design-guidelines.md)
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/600.css';

import App from './App';
import { getStorage, setStorage, STORAGE_KEY } from './helpers/storage';
import store from './helpers/store';
import Styleguide from './pages/Styleguide';
import reportWebVitals from './reportWebVitals';
import theme from './themes';
import { history } from './utils/history';

const setDefaultTheme = (defaultTheme: string) => {
  const existTheme = getStorage(STORAGE_KEY.CHAKRA_UI_COLOR_MODE);
  if (!existTheme) {
    setStorage(STORAGE_KEY.CHAKRA_UI_COLOR_MODE, defaultTheme);
  }
};

// Pass the app theme so the standalone toast doesn't inject Chakra's DEFAULT theme
// CSS vars (e.g. --chakra-fonts-body) into :root and override the DS fonts app-wide.
const { ToastContainer } = createStandaloneToast({ theme });

const container = document.getElementById('root');
const root = createRoot(container!);
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 1,
    },
  },
});
setDefaultTheme('dark');

// DS living styleguide — standalone, ngoài authed router (dev tool). Truy cập /styleguide.
if (window.location.pathname.startsWith('/styleguide')) {
  root.render(
    <ChakraProvider theme={theme}>
      <ColorModeScript initialColorMode='dark' />
      <Styleguide />
    </ChakraProvider>
  );
} else {
  root.render(
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ChakraProvider theme={theme}>
          <Router history={history}>
            <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js'>
              <ColorModeScript initialColorMode='dark' />
              <App />
              <ToastContainer />
            </Worker>
          </Router>
        </ChakraProvider>
      </Provider>
    </QueryClientProvider>
  );
}

reportWebVitals();

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}
